import React, { createContext, useState, ReactNode, useEffect } from 'react';
import { getCurrentRegion } from '../helpers/siteHelpers';
import { FALLBACK_REGION } from '../config/api';

// Region constants
export const REGION_US = "us";
export const REGION_NZ = "nz";
export const REGION_AU = "au";
export const REGION_MY = "my";
export const REGION_EU = "eu";
export const REGION_UK = "uk";
export const REGION_JP = "jp";
export const REGION_PH = "ph";
export const REGION_VN = "vn";
export const REGION_CA = "ca";
export const REGION_TH = "th";
export const REGION_EVENTS = "events";

export const REGION_LIST = [
  REGION_US,
  REGION_NZ,
  REGION_AU,
  REGION_MY,
  REGION_EU,
  REGION_UK,
  REGION_JP,
  REGION_PH,
  REGION_VN,
  REGION_CA,
  REGION_TH,
  REGION_EVENTS,
];

// Display name per region code. The `eu` code is permanent but the office behind it is Germany
// (OR-052), so it reads "Germany", not "Europe". Every hard-coded region label in the app reads
// this map; add a region here, not in a local copy.
export const REGION_LABELS: Record<string, string> = {
  [REGION_US]: "United States",
  [REGION_NZ]: "New Zealand",
  [REGION_AU]: "Australia",
  [REGION_MY]: "Malaysia",
  [REGION_EU]: "Germany",
  [REGION_UK]: "United Kingdom",
  [REGION_JP]: "Japan",
  [REGION_PH]: "Philippines",
  [REGION_VN]: "Vietnam",
  [REGION_CA]: "Canada",
  [REGION_TH]: "Thailand",
  [REGION_EVENTS]: "Events",
};

interface RegionContextType {
  selectedRegion: string;
  setSelectedRegion: (region: string) => void;
  availableRegions: string[];
  setAvailableRegions: (regions: string[]) => void;
}

export const RegionContext = createContext<RegionContextType | undefined>(undefined);

const REGION_STORAGE_KEY = 'pcg_selected_region';

interface RegionProviderProps {
  children: ReactNode;
}

export const RegionProvider: React.FC<RegionProviderProps> = ({ children }) => {
  const [selectedRegion, setSelectedRegionState] = useState<string>(() => {
    if (typeof window === 'undefined') {
      return FALLBACK_REGION;
    }
    // This office's own region (derived from hostname) takes priority — it's what every other API
    // call on this deployment already targets. Mirror getBaseUrl() in config/api.ts, which uses
    // getCurrentRegion() as-is for any non-"dev" value — INCLUDING scoped generic-dashboard regions
    // (e.g. dev3) that aren't in REGION_LIST. Gating on REGION_LIST here would send the PO hooks to
    // FALLBACK_REGION while the rest of the app hits the scoped region.
    const hostRegion = getCurrentRegion();
    if (hostRegion && hostRegion !== 'dev') {
      return hostRegion;
    }
    // Local dev / unknown hostnames: fall back to whatever was last picked (a real franchise region),
    // else the same region config/api.ts uses so both stay in sync.
    const stored = localStorage.getItem(REGION_STORAGE_KEY);
    return stored && REGION_LIST.includes(stored) ? stored : FALLBACK_REGION;
  });

  const [availableRegions, setAvailableRegions] = useState<string[]>(REGION_LIST);

  // Persist selection to localStorage whenever it changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(REGION_STORAGE_KEY, selectedRegion);
    }
  }, [selectedRegion]);

  const setSelectedRegion = (region: string) => {
    // Validate region before setting
    if (REGION_LIST.includes(region)) {
      setSelectedRegionState(region);
    } else {
      console.warn(`Invalid region: ${region}. Defaulting to ${REGION_US}`);
      setSelectedRegionState(REGION_US);
    }
  };

  const value: RegionContextType = {
    selectedRegion,
    setSelectedRegion,
    availableRegions,
    setAvailableRegions,
  };

  return (
    <RegionContext.Provider value={value}>
      {children}
    </RegionContext.Provider>
  );
};

export default RegionProvider;
